import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { base44 } from "@/api/base44Client";
import { toast } from "sonner";
import { 
  X, 
  Activity, 
  CheckSquare, 
  AlertTriangle, 
  User, 
  Plus,
  Zap,
  Clock,
  Shield,
  RotateCcw,
  MessageSquare,
  Radio,
  Target,
  ChevronDown,
  ChevronRight,
  MoreHorizontal,
  PauseCircle,
  AlertOctagon,
  CheckCircle2,
  Calendar as CalendarIcon,
  ArrowLeft,
  Users,
  LayoutGrid,
  FileText,
  ListTodo,
  AlertCircle,
  Maximize2,
  Minimize2
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { format, formatDistanceToNow } from 'date-fns';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import OfficerTaskInspector from './OfficerTaskInspector';
import ProgressReportModal from './ProgressReportModal';
import ActivityIssuesSection from './ActivityIssuesSection';
import ActivityTaskLogView from './ActivityTaskLogView';
import TaskQuickAdd from './TaskQuickAdd';
import ReportActivityIssueModal from './ReportActivityIssueModal';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ConfirmableDialog from '@/components/common/ConfirmableDialog';
import { Rocket, Compass, Bot, CheckCheck, Pencil, Archive, Trash2 } from 'lucide-react';
import BotTaskGenerationSection from '@/components/activities/BotTaskGenerationSection';
import KRProgressChart from './KRProgressChart';
import useDeleteConfirmation from '@/components/hooks/useDeleteConfirmation';

export default function ActivityRTSOverlay({ activity: initialActivity, onClose, officers }) {
  const queryClient = useQueryClient();
  
  // View State: 'home', 'raci', 'issues', 'tasks', 'reports'
  const [currentView, setCurrentView] = useState('home');

  // Live data
  const { data: activity } = useQuery({
    queryKey: ['activity', initialActivity?.id],
    queryFn: () => base44.entities.Activity.get(initialActivity?.id),
    initialData: initialActivity,
    refetchInterval: 2000
  });

  // State
  const [showReportIssueModal, setShowReportIssueModal] = useState(false);
  const [showHierarchyDialog, setShowHierarchyDialog] = useState(false);
  const [showKRDialog, setShowKRDialog] = useState(false);
  const [showBotTaskDialog, setShowBotTaskDialog] = useState(false);
  const [reportIssueInitialData, setReportIssueInitialData] = useState(null);
  const [inspectingOfficer, setInspectingOfficer] = useState(null);
  const [showProgressReportModal, setShowProgressReportModal] = useState(false);
  const [showAddTaskDialog, setShowAddTaskDialog] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [currentUserEmail, setCurrentUserEmail] = useState(null);
  const [editingReport, setEditingReport] = useState(null);

  // Fetch current user
  React.useEffect(() => {
    base44.auth.me().then(user => setCurrentUserEmail(user?.email)).catch(() => {});
  }, []);
  
  // Data Fetching
  const { data: tasks = [] } = useQuery({
    queryKey: ['dailyTasks', activity?.id],
    queryFn: async () => {
      const allTasks = await base44.entities.DailyTask.list();
      return allTasks.filter(t => t.activity_id === activity?.id);
    },
    enabled: !!activity?.id,
    refetchInterval: 5000
  });

  const { data: issues = [] } = useQuery({
    queryKey: ['issues', activity?.id],
    queryFn: async () => {
      const allIssues = await base44.entities.Issue.list();
      return allIssues.filter(i => i.linked_activity_id === activity?.id && i.status !== 'resolved');
    },
    enabled: !!activity?.id,
    refetchInterval: 5000
  });

  const { data: progressReports = [] } = useQuery({
    queryKey: ['progressReports', activity?.id],
    queryFn: async () => {
      const allReports = await base44.entities.ProgressReport.list();
      return allReports
          .filter(r => r.activity_id === activity.id)
          .sort((a, b) => new Date(b.reported_at) - new Date(a.reported_at));
    },
    enabled: !!activity?.id
  });

  const { data: parentObjective } = useQuery({
    queryKey: ['objective', activity?.objective_id],
    queryFn: () => base44.entities.Objective.get(activity?.objective_id),
    enabled: !!activity?.objective_id && showHierarchyDialog
  });

  const { data: parentMission } = useQuery({
    queryKey: ['mission', parentObjective?.mission_id],
    queryFn: () => base44.entities.Mission.get(parentObjective?.mission_id),
    enabled: !!parentObjective?.mission_id
  });
  
  // Permission check for RACI editing: only mission sponsor, mission owner, or activity accountable
  const canEditRaci = currentUserEmail && (
    currentUserEmail === parentMission?.sponsor_email ||
    currentUserEmail === parentMission?.owner_email ||
    currentUserEmail === activity?.accountable_email
  );

  // Entities for Tagging
  const { data: artifacts = [] } = useQuery({ queryKey: ['artifacts'], queryFn: () => base44.entities.Artifact.list(), enabled: showReportIssueModal });
  const { data: rituals = [] } = useQuery({ queryKey: ['rituals'], queryFn: () => base44.entities.Ritual.list(), enabled: showReportIssueModal });
  const { data: policies = [] } = useQuery({ queryKey: ['policies'], queryFn: () => base44.entities.Policy.list(), enabled: showReportIssueModal });
  const { data: capabilities = [] } = useQuery({ queryKey: ['capabilities'], queryFn: () => base44.entities.Capability.list(), enabled: showReportIssueModal });

  // Mutations
  const createProgressReportMutation = useMutation({
    mutationFn: async (reportData) => {
        const user = await base44.auth.me();
        return base44.entities.ProgressReport.create({
            ...reportData,
            reported_by: user.email,
            approved: false
        });
    },
    onSuccess: async () => {
        // Don't update key results values until report is approved
        queryClient.invalidateQueries({ queryKey: ['progressReports', activity.id] });
        setShowProgressReportModal(false);
    }
  });

  const updateTaskMutation = useMutation({
    mutationFn: ({ id, ...updates }) => base44.entities.DailyTask.update(id, updates),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['dailyTasks'] })
  });
  
  const updateActivityMutation = useMutation({
    mutationFn: (updates) => base44.entities.Activity.update(activity.id, updates),
    onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['civilization_map_data'] }); 
        queryClient.invalidateQueries({ queryKey: ['activity', activity.id] });
    }
  });

  const approveReportMutation = useMutation({
    mutationFn: async (report) => {
      // Update the report as approved
      await base44.entities.ProgressReport.update(report.id, { 
        approved: true,
        approved_by: currentUserEmail,
        approved_at: new Date().toISOString()
      });
      
      // Update key results values NOW that report is approved
      const updatedKeyResults = activity.key_results.map(kr => {
        const update = report.key_results_progress.find(u => u.description === kr.description);
        return update ? { ...kr, current_value: update.new_value } : kr;
      });
      
      // Calculate key_results_progress percentage
      let key_results_progress = 0;
      if (updatedKeyResults.length > 0) {
        const totalProgress = updatedKeyResults.reduce((sum, kr) => {
          const current = parseFloat(kr.current_value) || 0;
          const target = parseFloat(kr.target) || 1;
          return sum + (target > 0 ? (current / target) * 100 : 0);
        }, 0);
        key_results_progress = Math.round(totalProgress / updatedKeyResults.length);
      }
      
      // Update the activity status flag and key results
      await base44.entities.Activity.update(activity.id, {
        status_flag: report.proposed_status_flag,
        status_flag_updated_at: new Date().toISOString(),
        status_flag_updated_by: currentUserEmail,
        key_results: updatedKeyResults,
        key_results_progress
      });

      // NOTE: Issue creation is handled by the ReportIssue entity automation
      // (promoteReportIssue). Do NOT create Issues directly here to avoid duplicates.
      // The automation includes duplicate detection within a 24-hour window.
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['progressReports', activity.id] });
      queryClient.invalidateQueries({ queryKey: ['activity', activity.id] });
      toast.success('Progress report approved');
    },
    onError: () => {
      toast.error('Failed to approve report');
    }
  });

  const archiveReportMutation = useMutation({
    mutationFn: async (reportId) => {
      await base44.entities.ProgressReport.update(reportId, { archived: true });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['progressReports', activity.id] });
      toast.success('Progress report archived');
    },
    onError: () => {
      toast.error('Failed to archive report');
    }
  });

  // Use the unified delete confirmation hook for progress reports
  const {
    handleDeleteClick: handleReportDeleteClick,
    isPendingConfirm: isReportPendingConfirm,
    isDeleting: isReportDeleting
  } = useDeleteConfirmation({
    entityType: 'ProgressReport',
    queryKey: 'progressReports',
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['progressReports', activity?.id] });
      toast.success('Progress report removed');
    },
    onError: () => {
      toast.error('Failed to remove report');
    }
  });

  // Handlers
  const openReportModal = (initialData = null) => {
      setReportIssueInitialData(initialData);
      setShowReportIssueModal(true);
  };

  const handleTaskStruggling = async (task) => {
      updateTaskMutation.mutate({ 
        id: task.id, 
        status_flag: 'red',
        started_at: null,
        blocked_at: new Date().toISOString()
      });
      openReportModal({
          title: `Blocker: ${task.task_description}`,
          description: `Struggling with task: ${task.task_description}`,
          source: 'task',
          source_id: task.id
      });
  };

  const handleRaciUpdate = (field, value) => {
      updateActivityMutation.mutate({ [field]: value });
  };

  // Helpers
  const getOfficer = (email) => officers.find(o => o.user_email === email) || { user_email: email, callsign: email?.split('@')[0] };

  const formatOfficerName = (email) => {
      const officer = getOfficer(email);
      return `${officer.callsign || 'Unknown'}, ${officer.rank_title || 'Cadet'}, Lvl ${officer.clearance_level || 1}`;
  };

  const getStatusColor = (status) => {
      switch(status) {
          case 'green': return 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30';
          case 'yellow': return 'text-amber-400 bg-amber-500/10 border-amber-500/30';
          case 'red': return 'text-rose-400 bg-rose-500/10 border-rose-500/30';
          case 'gray': return 'text-slate-400 bg-slate-500/10 border-slate-500/30';
          default: return 'text-slate-400 bg-slate-800 border-slate-700';
      }
  };

  // Metrics Calculation
  const krProgress = (activity.key_results || []).reduce((acc, kr) => {
      const c = parseFloat(kr.current_value) || 0;
      const t = parseFloat(kr.target) || 1;
      return acc + (t > 0 ? (c / t) : 0);
  }, 0) / Math.max((activity.key_results || []).length, 1) * 100;

  const taskStats = tasks.reduce((acc, t) => {
      if (t.status_flag === 'green') acc.conquered++;
      else if (t.status_flag === 'red') acc.struggling++;
      else if (t.status_flag === 'yellow') acc.imminent++;
      else acc.notStarted++;
      return acc;
  }, { conquered: 0, imminent: 0, struggling: 0, notStarted: 0 });
  
  const totalTasks = tasks.length || 1;

  const [showCloseConfirm, setShowCloseConfirm] = React.useState(false);
  const [isFullscreen, setIsFullscreen] = React.useState(false);

  if (!activity) return null;

  const accountableOfficer = getOfficer(activity.accountable_email);
  const responsibleOfficer = getOfficer(activity.responsible_email);

  const handleBackdropClick = () => {
    setShowCloseConfirm(true);
  };

  const handleConfirmClose = () => {
    setShowCloseConfirm(false);
    onClose();
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.98, y: 10 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.98, y: 10 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 pointer-events-none"
    >
      <div data-testid="civ-activityrtsoverlay-clickable-div" className="absolute inset-0 bg-[#000000]/80 backdrop-blur-[4px] pointer-events-auto transition-opacity" onClick={handleBackdropClick} />
      
      {/* RTS Grid Background Overlay */}
      <div className="absolute inset-0 pointer-events-none opacity-10 bg-[radial-gradient(#4f46e5_1px,transparent_1px)] [background-size:16px_16px] z-0" />

      <div className={cn(
        "relative z-10 bg-[#0B1221] border border-slate-700/50 shadow-[0_0_50px_-12px_rgba(79,70,229,0.3)] overflow-hidden pointer-events-auto flex flex-col transition-all duration-300",
        isFullscreen 
          ? "w-full h-full max-w-none max-h-none rounded-none" 
          : "w-full max-w-2xl rounded-xl max-h-[90vh] sm:max-h-[85vh]"
      )}>
        
        {/* 1. HEADER */}
        <div className="px-4 pt-3 pb-1 border-b border-slate-800 bg-[#0F172A]/80 backdrop-blur shrink-0 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-indigo-500/50 to-transparent" />
          <div className="flex items-start justify-between gap-4 relative z-10">
            <div className="space-y-1 flex-1">
              <div className="flex items-center gap-3">
                 <Badge variant="outline" className={cn("text-[10px] px-1.5 py-0 uppercase tracking-widest border font-bold h-5 shadow-[0_0_10px_-3px_currentColor]", getStatusColor(activity.status_flag || 'yellow'))}>
                    <motion.span 
                       animate={activity.status_flag !== 'green' ? { opacity: [1, 0.5, 1] } : {}} 
                       transition={{ repeat: Infinity, duration: 4 }}
                    >
                       {activity.status_flag === 'green' ? 'On Track' : activity.status_flag === 'red' ? 'Off Track' : 'At Risk'}
                    </motion.span>
                 </Badge>
                 <Badge variant="outline" className="text-[10px] px-1.5 py-0 uppercase tracking-widest border font-bold h-5 text-slate-400 border-slate-600">
                    KR: {activity.key_results_progress || 0}%
                 </Badge>
                 <Badge variant="outline" className="text-[10px] px-1.5 py-0 uppercase tracking-widest border font-bold h-5 text-slate-400 border-slate-600">
                    <CalendarIcon className="w-3 h-3 mr-1" />
                    Due {activity.due_date ? format(new Date(activity.due_date), 'MMM d') : 'TBD'}
                 </Badge>
              </div>
              <h2 className="text-lg font-bold text-slate-100 tracking-tight leading-tight flex items-center gap-2">
                  <span 
                    className="text-indigo-400 font-mono text-base font-normal cursor-pointer hover:text-indigo-300 hover:underline transition-colors"
                    onClick={() => setShowHierarchyDialog(true)}
                  >
                    [{activity.activity_code || 'ACT'}]
                  </span>
                  {activity.title}
              </h2>
            </div>
            <div className="flex items-center gap-1 -mr-2 -mt-1">
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => setIsFullscreen(!isFullscreen)} 
                className="h-8 w-8 hover:bg-slate-800 text-slate-400"
                title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
              >
                {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
              </Button>
              <Button variant="ghost" size="icon" onClick={handleBackdropClick} className="h-8 w-8 hover:bg-slate-800 text-slate-400">
                <X className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>

        {/* 2. SUMMARY DATA (Top Bar) */}
        <div className="px-4 py-3 bg-[#0B1221] border-b border-slate-800 space-y-3">
           {/* Line 1: KR Progress */}
           <div data-testid="civ-activityrtsoverlay-clickable-div-1" className="flex items-center gap-3 w-full cursor-pointer hover:bg-slate-800/30 rounded px-2 -mx-2 py-1 transition-colors" onClick={() => setShowKRDialog(true)}>
              <span className="text-xs font-bold text-slate-500 w-8 hover:text-indigo-400">KR</span>
              <div className="flex-1 h-2.5 bg-slate-800 rounded-full overflow-hidden relative">
                  <motion.div 
                    className={cn(
                      "h-full",
                      (activity.key_results_progress || 0) >= 70 ? 'bg-emerald-500' :
                      (activity.key_results_progress || 0) >= 40 ? 'bg-amber-500' : 'bg-rose-500'
                    )}
                    initial={{ width: 0 }}
                    animate={{ width: `${activity.key_results_progress || 0}%` }}
                    transition={{ duration: 1, ease: "easeOut" }}
                  />
              </div>
              <span className="text-xs text-slate-500 font-mono w-10 text-right">{activity.key_results_progress || 0}%</span>
           </div>

           {/* Line 2: DT, Issues, PR */}
           <div className="flex items-center gap-4 w-full h-7">
              {/* DT Bar (50%) */}
              <div data-testid="civ-activityrtsoverlay-clickable-div-2" className="flex items-center gap-3 w-1/2 cursor-pointer hover:bg-slate-800/30 rounded px-2 -mx-2 py-1 transition-colors" onClick={() => setCurrentView('tasks')}>
                  <span className="text-xs font-bold text-slate-500 w-8 hover:text-indigo-400">DT</span>
                  <div className="flex-1 h-2.5 bg-slate-800 rounded-full overflow-hidden flex">
                      {/* Conquered */}
                      <div className="h-full bg-emerald-500" style={{ width: `${(taskStats.conquered / totalTasks) * 100}%` }} />
                      {/* Imminent */}
                      <div className="h-full bg-amber-500" style={{ width: `${(taskStats.imminent / totalTasks) * 100}%` }} />
                      {/* Struggling */}
                      <div className="h-full bg-rose-500" style={{ width: `${(taskStats.struggling / totalTasks) * 100}%` }} />
                      {/* Not Started */}
                      <div className="h-full bg-slate-700" style={{ width: `${(taskStats.notStarted / totalTasks) * 100}%` }} />
                  </div>
              </div>

              {/* Issues Icon (15%) */}
              <div data-testid="civ-activityrtsoverlay-clickable-div-3" className="w-[15%] flex items-center gap-1.5 justify-center border-l border-r border-slate-800 px-3 cursor-pointer hover:bg-slate-800/30 rounded transition-colors" onClick={() => setCurrentView('issues')}>
                 <AlertCircle className={cn("w-4 h-4", issues.length > 0 ? "text-rose-400" : "text-slate-600")} />
                 <span className={cn("text-sm font-bold", issues.length > 0 ? "text-rose-400" : "text-slate-600")}>{issues.length}</span>
              </div>

              {/* PR Stats (Rest) - Only show approved reports */}
              <div data-testid="civ-activityrtsoverlay-clickable-div-4" className="flex-1 flex items-center justify-end gap-2 overflow-hidden cursor-pointer hover:bg-slate-800/30 rounded px-2 py-1 transition-colors" onClick={() => setCurrentView('reports')}>
                 <span className="text-xs font-bold text-slate-500 hover:text-amber-400">PR</span>
                 {(() => {
                    const approvedReports = progressReports.filter(r => r.approved);
                    return approvedReports.length > 0 ? (
                       <>
                          <span className="text-[11px] text-slate-400 truncate max-w-[70px]">
                            {formatDistanceToNow(new Date(approvedReports[0].approved_at || approvedReports[0].reported_at))}
                          </span>
                          <div className="flex gap-1">
                             {approvedReports.slice(0, 5).map((pr, i) => (
                                <div 
                                   key={i} 
                                   className={cn(
                                      "w-2 h-2 rounded-full",
                                      pr.proposed_status_flag === 'green' ? "bg-emerald-500" :
                                      pr.proposed_status_flag === 'red' ? "bg-rose-500" : "bg-amber-500"
                                   )}
                                />
                             ))}
                          </div>
                       </>
                    ) : (
                       <span className="text-[11px] text-slate-600 italic">None</span>
                    );
                 })()}
              </div>
           </div>
        </div>

        {/* 3. MAIN CONTENT AREA */}
        <div className="flex-1 min-h-0 bg-[#0B1221] relative overflow-y-auto [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-slate-700/50 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-slate-600/50">
          <AnimatePresence mode="wait">
            {currentView === 'home' && (
               <motion.div 
                 key="home"
                 initial={{ opacity: 0 }}
                 animate={{ opacity: 1 }}
                 exit={{ opacity: 0 }}
                 className="h-full min-h-[400px] flex flex-col items-center justify-center p-6 relative overflow-y-auto"
               >
                   {/* Center Circles */}
                   <div className="flex items-center gap-12 relative z-10 mt-4">
                       {/* Accountable */}
                       <div className="flex flex-col items-center gap-3">
                           <span className="text-4xl font-black text-indigo-500/20 absolute -top-12">A</span>
                           <span className="text-xs font-bold text-indigo-400 uppercase tracking-widest bg-[#0B1221] px-2 z-10">Accountable</span>
                           <div 
                              className="w-24 h-24 rounded-full border-4 border-indigo-500/30 p-1 cursor-pointer hover:border-indigo-400 hover:scale-105 transition-all shadow-[0_0_30px_-5px_rgba(99,102,241,0.3)]"
                              onClick={() => setInspectingOfficer(accountableOfficer)}
                           >
                               <div className="w-full h-full rounded-full bg-indigo-900/20 overflow-hidden relative">
                                  {accountableOfficer.avatar_url ? (
                                     <img src={accountableOfficer.avatar_url} className="w-full h-full object-cover" alt="Accountable" />
                                  ) : (
                                     <div className="w-full h-full flex items-center justify-center text-indigo-500 font-bold text-2xl">
                                        {accountableOfficer.callsign?.substring(0,1)}
                                     </div>
                                  )}
                               </div>
                           </div>
                           <div className="text-center">
                               <div className="text-lg font-bold text-white">{accountableOfficer.callsign}</div>
                               <div className="text-[10px] text-slate-400 uppercase tracking-wider">
                                  {accountableOfficer.rank_title} • Lvl {accountableOfficer.clearance_level}
                               </div>
                           </div>
                       </div>

                       {/* Connector Line */}
                       <div className="h-[2px] w-16 bg-gradient-to-r from-indigo-500/30 to-emerald-500/30 absolute top-[4.5rem] left-1/2 -translate-x-1/2" />

                       {/* Responsible */}
                       <div className="flex flex-col items-center gap-3">
                           <span className="text-4xl font-black text-emerald-500/20 absolute -top-12">R</span>
                           <span className="text-xs font-bold text-emerald-400 uppercase tracking-widest bg-[#0B1221] px-2 z-10">Responsible</span>
                           <div className="relative">
                              <div 
                                 className="w-24 h-24 rounded-full border-4 border-emerald-500/30 p-1 cursor-pointer hover:border-emerald-400 hover:scale-105 transition-all shadow-[0_0_30px_-5px_rgba(16,185,129,0.3)]"
                                 onClick={() => setInspectingOfficer(responsibleOfficer)}
                              >
                                  <div className="w-full h-full rounded-full bg-emerald-900/20 overflow-hidden relative">
                                     {responsibleOfficer.avatar_url ? (
                                        <img src={responsibleOfficer.avatar_url} className="w-full h-full object-cover" alt="Responsible" />
                                     ) : (
                                        <div className="w-full h-full flex items-center justify-center text-emerald-500 font-bold text-2xl">
                                           {responsibleOfficer.callsign?.substring(0,1)}
                                        </div>
                                     )}
                                  </div>
                              </div>
                              {/* Bot Icon - Shows when responsible is AI Officer */}
                              {responsibleOfficer.is_bot && (
                                 <button
                                    onClick={(e) => {
                                       e.stopPropagation();
                                       setShowBotTaskDialog(true);
                                    }}
                                    className="absolute -right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-purple-600 hover:bg-purple-500 border-2 border-[#0B1221] shadow-lg flex items-center justify-center transition-all hover:scale-110"
                                    title="AI Task Generation"
                                 >
                                    <Bot className="w-5 h-5 text-white" />
                                 </button>
                              )}
                           </div>
                           <div className="text-center">
                               <div className="text-lg font-bold text-white flex items-center gap-2 justify-center">
                                  {responsibleOfficer.callsign}
                                  {responsibleOfficer.is_bot && <Bot className="w-4 h-4 text-purple-400" />}
                               </div>
                               <div className="text-[10px] text-slate-400 uppercase tracking-wider">
                                  {responsibleOfficer.rank_title} • Lvl {responsibleOfficer.clearance_level}
                               </div>
                           </div>
                       </div>
                   </div>

                   {/* Navigation Icons (Orbiting) */}
                   <div className="absolute inset-0 pointer-events-none">
                       {/* Top Left - RACI */}
                       <div className="absolute top-8 left-8 pointer-events-auto">
                           <Button 
                              variant="ghost" 
                              className="flex flex-col gap-2 h-auto w-32 hover:bg-transparent group"
                              onClick={() => setCurrentView('raci')}
                           >
                               <div className="w-20 h-20 rounded-xl bg-slate-800/50 border border-slate-700 flex items-center justify-center group-hover:border-cyan-400 group-hover:bg-cyan-950/30 transition-all shadow-lg">
                                   <LayoutGrid className="w-10 h-10 text-slate-400 group-hover:text-cyan-400" />
                               </div>
                               <span className="text-[10px] font-bold text-slate-500 group-hover:text-cyan-400 uppercase">RACI Matrix</span>
                           </Button>
                       </div>

                       {/* Top Right - Issues */}
                       <div className="absolute top-8 right-8 pointer-events-auto">
                           <Button 
                              variant="ghost" 
                              className="flex flex-col gap-2 h-auto w-32 hover:bg-transparent group"
                              onClick={() => setCurrentView('issues')}
                           >
                               <div className="w-20 h-20 rounded-xl bg-slate-800/50 border border-slate-700 flex items-center justify-center group-hover:border-rose-400 group-hover:bg-rose-950/30 transition-all shadow-lg relative">
                                   <AlertOctagon className="w-10 h-10 text-slate-400 group-hover:text-rose-400" />
                                   {issues.length > 0 && (
                                      <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-rose-500 flex items-center justify-center text-xs text-white font-bold border border-[#0B1221]">
                                         {issues.length}
                                      </div>
                                   )}
                               </div>
                               <span className="text-[10px] font-bold text-slate-500 group-hover:text-rose-400 uppercase">Open Issues</span>
                           </Button>
                       </div>

                       {/* Bottom Left - Tasks */}
                       <div className="absolute bottom-8 left-8 pointer-events-auto">
                           <Button 
                              variant="ghost" 
                              className="flex flex-col gap-2 h-auto w-32 hover:bg-transparent group"
                              onClick={() => setCurrentView('tasks')}
                           >
                               <div className="w-20 h-20 rounded-xl bg-slate-800/50 border border-slate-700 flex items-center justify-center group-hover:border-indigo-400 group-hover:bg-indigo-950/30 transition-all shadow-lg">
                                   <ListTodo className="w-10 h-10 text-slate-400 group-hover:text-indigo-400" />
                               </div>
                               <span className="text-[10px] font-bold text-slate-500 group-hover:text-indigo-400 uppercase">Daily Task Log</span>
                           </Button>
                       </div>

                       {/* Bottom Right - Reports */}
                       <div className="absolute bottom-8 right-8 pointer-events-auto">
                           <Button 
                              variant="ghost" 
                              className="flex flex-col gap-2 h-auto w-32 hover:bg-transparent group"
                              onClick={() => setCurrentView('reports')}
                           >
                               <div className="w-20 h-20 rounded-xl bg-slate-800/50 border border-slate-700 flex items-center justify-center group-hover:border-amber-400 group-hover:bg-amber-950/30 transition-all shadow-lg">
                                   <FileText className="w-10 h-10 text-slate-400 group-hover:text-amber-400" />
                               </div>
                               <span className="text-[10px] font-bold text-slate-500 group-hover:text-amber-400 uppercase">Progress Reports</span>
                           </Button>
                       </div>
                   </div>
               </motion.div>
            )}

            {currentView === 'raci' && (
               <motion.div key="raci" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="p-4 h-full">
                   <div className="flex items-center gap-2 mb-4">
                       <Button variant="ghost" size="icon" onClick={() => setCurrentView('home')} className="h-8 w-8 hover:bg-slate-800">
                           <ArrowLeft className="w-4 h-4" />
                       </Button>
                       <h3 className="text-sm font-bold text-cyan-400 uppercase tracking-widest flex items-center gap-2">
                           <LayoutGrid className="w-4 h-4" /> RACI Matrix
                       </h3>
                   </div>
                   
                   <div className="bg-slate-900/30 border border-slate-800/50 rounded-lg p-4 h-full">
                       {/* Reusing existing RACI logic but expanded */}
                       <div className="grid grid-cols-1 gap-6">
                           <div className="grid grid-cols-2 gap-6">
                               {/* Accountable */}
                               <div className="space-y-2">
                                   <div className="text-xs font-bold text-slate-500 uppercase tracking-widest border-b border-slate-800 pb-1">Accountable</div>
                                   <div className="flex items-center gap-3 bg-slate-900/50 p-3 rounded-lg border border-slate-800">
                                       <div data-testid="civ-activityrtsoverlay-clickable-div-5" className="w-10 h-10 rounded-full bg-indigo-900/30 border border-indigo-500/30 flex items-center justify-center text-indigo-400 font-bold" onClick={() => setInspectingOfficer(getOfficer(activity.accountable_email))}>
                                           {getOfficer(activity.accountable_email).avatar_url ? <img src={getOfficer(activity.accountable_email).avatar_url} className="w-full h-full rounded-full object-cover" /> : 'A'}
                                       </div>
                                       <div>
                                           <div className="text-sm font-bold text-slate-200">{getOfficer(activity.accountable_email).callsign}</div>
                                           <div className="text-[10px] text-slate-500 uppercase">{getOfficer(activity.accountable_email).rank_title}</div>
                                       </div>
                                   </div>
                               </div>

                               {/* Responsible */}
                               <div className="space-y-2">
                                   <div className="text-xs font-bold text-slate-500 uppercase tracking-widest border-b border-slate-800 pb-1">Responsible</div>
                                   <div className="flex items-center justify-between gap-3 bg-slate-900/50 p-3 rounded-lg border border-slate-800 group hover:border-emerald-500/30 transition-colors">
                                       <div className="flex items-center gap-3">
                                           <div data-testid="civ-activityrtsoverlay-clickable-div-6" className="w-10 h-10 rounded-full bg-emerald-900/30 border border-emerald-500/30 flex items-center justify-center text-emerald-400 font-bold" onClick={() => setInspectingOfficer(getOfficer(activity.responsible_email))}>
                                               {getOfficer(activity.responsible_email).avatar_url ? <img src={getOfficer(activity.responsible_email).avatar_url} className="w-full h-full rounded-full object-cover" /> : 'R'}
                                           </div>
                                           <div>
                                               <div className="text-sm font-bold text-slate-200">{getOfficer(activity.responsible_email).callsign}</div>
                                               <div className="text-[10px] text-slate-500 uppercase">{getOfficer(activity.responsible_email).rank_title}</div>
                                           </div>
                                       </div>
                                       {canEditRaci && (
                                         <DropdownMenu>
                                             <DropdownMenuTrigger asChild>
                                                 <Button variant="ghost" size="sm" className="h-6 text-[10px] px-2 text-slate-500 hover:text-emerald-400 hover:bg-emerald-950/30">
                                                     Change
                                                 </Button>
                                             </DropdownMenuTrigger>
                                             <DropdownMenuContent className="bg-slate-950 border-slate-800 text-slate-100 max-h-60 overflow-y-auto w-64">
                                                 {officers.filter(officer => {
                                                     if (!officer.is_bot) return true;
                                                     return officer.bot_owner_email === currentUserEmail;
                                                 }).map(officer => (
                                                     <DropdownMenuItem 
                                                         key={officer.user_email} 
                                                         onClick={() => handleRaciUpdate('responsible_email', officer.user_email)}
                                                         className="text-xs focus:bg-slate-900 focus:text-emerald-400 cursor-pointer"
                                                     >
                                                         {formatOfficerName(officer.user_email)}
                                                     </DropdownMenuItem>
                                                 ))}
                                             </DropdownMenuContent>
                                         </DropdownMenu>
                                       )}
                                   </div>
                               </div>
                           </div>

                           <div className="grid grid-cols-2 gap-6 pt-4 border-t border-slate-800/50">
                               {/* Consulted */}
                               <div className="space-y-2">
                                   <div className="flex items-center justify-between border-b border-slate-800 pb-1">
                                       <div className="text-xs font-bold text-slate-500 uppercase tracking-widest">Consulted</div>
                                       {canEditRaci && (
                                         <DropdownMenu>
                                             <DropdownMenuTrigger asChild>
                                                 <Button variant="ghost" size="sm" className="h-5 w-5 p-0 rounded-full bg-slate-800 text-slate-400 hover:bg-amber-900/30 hover:text-amber-400">
                                                     <Plus className="w-3 h-3" />
                                                 </Button>
                                             </DropdownMenuTrigger>
                                             <DropdownMenuContent className="bg-slate-950 border-slate-800 text-slate-100 max-h-60 overflow-y-auto w-64">
                                                 {officers.map(officer => {
                                                     const isSelected = (activity.consulted_emails || []).includes(officer.user_email);
                                                     if (isSelected) return null;
                                                     return (
                                                         <DropdownMenuItem 
                                                             key={officer.user_email} 
                                                             onClick={() => handleRaciUpdate('consulted_emails', [...(activity.consulted_emails || []), officer.user_email])}
                                                             className="text-xs focus:bg-slate-900 focus:text-amber-400 cursor-pointer"
                                                         >
                                                             {formatOfficerName(officer.user_email)}
                                                         </DropdownMenuItem>
                                                     );
                                                 })}
                                             </DropdownMenuContent>
                                         </DropdownMenu>
                                       )}
                                   </div>
                                   <div className="space-y-2">
                                       {(activity.consulted_emails || []).map(email => (
                                           <div key={email} className="flex items-center justify-between bg-slate-900/30 px-3 py-2 rounded border border-slate-800/50">
                                               <div className="flex items-center gap-3">
                                                   <div className="w-8 h-8 rounded-full bg-amber-900/30 border border-amber-500/30 flex items-center justify-center text-amber-500 text-xs font-bold">
                                                       {getOfficer(email).callsign?.substring(0,1)}
                                                   </div>
                                                   <div>
                                                       <div className="text-xs font-medium text-slate-200">{getOfficer(email).callsign}</div>
                                                       <div className="text-[9px] text-slate-500">{getOfficer(email).rank_title}</div>
                                                   </div>
                                               </div>
                                                   {canEditRaci && (
                                                     <button 
                                                         onClick={() => handleRaciUpdate('consulted_emails', (activity.consulted_emails || []).filter(e => e !== email))}
                                                         className="text-slate-600 hover:text-rose-400"
                                                     >
                                                         <X className="w-4 h-4" />
                                                     </button>
                                                   )}
                                               </div>
                                               ))}
                                               {(activity.consulted_emails || []).length === 0 && (
                                               <div className="text-xs text-slate-600 italic px-2 py-4 text-center border border-dashed border-slate-800 rounded">No consulted officers</div>
                                               )}
                                               </div>
                                               </div>

                                               {/* Informed */}
                                               <div className="space-y-2">
                                               <div className="flex items-center justify-between border-b border-slate-800 pb-1">
                                               <div className="text-xs font-bold text-slate-500 uppercase tracking-widest">Informed</div>
                                               {canEditRaci && (
                                               <DropdownMenu>
                                                 <DropdownMenuTrigger asChild>
                                                     <Button variant="ghost" size="sm" className="h-5 w-5 p-0 rounded-full bg-slate-800 text-slate-400 hover:bg-cyan-900/30 hover:text-cyan-400">
                                                         <Plus className="w-3 h-3" />
                                                     </Button>
                                                 </DropdownMenuTrigger>
                                                 <DropdownMenuContent className="bg-slate-950 border-slate-800 text-slate-100 max-h-60 overflow-y-auto w-64">
                                                     {officers.map(officer => {
                                                         const isSelected = (activity.informed_emails || []).includes(officer.user_email);
                                                         if (isSelected) return null;
                                                         return (
                                                             <DropdownMenuItem 
                                                                 key={officer.user_email} 
                                                                 onClick={() => handleRaciUpdate('informed_emails', [...(activity.informed_emails || []), officer.user_email])}
                                                                 className="text-xs focus:bg-slate-900 focus:text-cyan-400 cursor-pointer"
                                                             >
                                                                 {formatOfficerName(officer.user_email)}
                                                             </DropdownMenuItem>
                                                         );
                                                     })}
                                                 </DropdownMenuContent>
                                               </DropdownMenu>
                                               )}
                                               </div>
                                   <div className="space-y-2">
                                       {(activity.informed_emails || []).map(email => (
                                           <div key={email} className="flex items-center justify-between bg-slate-900/30 px-3 py-2 rounded border border-slate-800/50">
                                               <div className="flex items-center gap-3">
                                                   <div className="w-8 h-8 rounded-full bg-cyan-900/30 border border-cyan-500/30 flex items-center justify-center text-cyan-500 text-xs font-bold">
                                                       {getOfficer(email).callsign?.substring(0,1)}
                                                   </div>
                                                   <div>
                                                       <div className="text-xs font-medium text-slate-200">{getOfficer(email).callsign}</div>
                                                       <div className="text-[9px] text-slate-500">{getOfficer(email).rank_title}</div>
                                                   </div>
                                               </div>
                                                   {canEditRaci && (
                                                     <button 
                                                         onClick={() => handleRaciUpdate('informed_emails', (activity.informed_emails || []).filter(e => e !== email))}
                                                         className="text-slate-600 hover:text-rose-400"
                                                     >
                                                         <X className="w-4 h-4" />
                                                     </button>
                                                   )}
                                               </div>
                                               ))}
                                               {(activity.informed_emails || []).length === 0 && (
                                               <div className="text-xs text-slate-600 italic px-2 py-4 text-center border border-dashed border-slate-800 rounded">No informed officers</div>
                                               )}
                                               </div>
                                               </div>
                                               </div>
                                               </div>
                                               </div>
                                               </motion.div>
                                               )}

            {currentView === 'issues' && (
               <motion.div key="issues" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="p-4 h-full">
                   <div className="flex items-center gap-2 mb-4">
                       <Button variant="ghost" size="icon" onClick={() => setCurrentView('home')} className="h-8 w-8 hover:bg-slate-800">
                           <ArrowLeft className="w-4 h-4" />
                       </Button>
                       <h3 className="text-sm font-bold text-rose-400 uppercase tracking-widest flex items-center gap-2">
                           <AlertOctagon className="w-4 h-4" /> Active Open Issues
                       </h3>
                       <Button 
                           size="sm" 
                           variant="ghost" 
                           className="ml-auto text-rose-400 hover:bg-rose-950/20 hover:text-rose-300 text-xs h-7"
                           onClick={() => openReportModal(null)}
                       >
                           <Plus className="w-3 h-3 mr-1" /> Report New
                       </Button>
                   </div>
                   <div className="bg-slate-900/30 border border-slate-800/50 rounded-lg p-2 h-[calc(100%-3rem)] overflow-hidden">
                       <ActivityIssuesSection 
                           activity={activity} 
                           officers={officers}
                           onReportIssue={() => openReportModal(null)}
                       />
                   </div>
               </motion.div>
            )}

            {currentView === 'tasks' && (
               <motion.div key="tasks" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="p-4 flex flex-col h-full">
                   <div className="flex items-center gap-2 mb-4 shrink-0">
                       <Button variant="ghost" size="icon" onClick={() => setCurrentView('home')} className="h-8 w-8 hover:bg-slate-800">
                           <ArrowLeft className="w-4 h-4" />
                       </Button>
                       <h3 className="text-sm font-bold text-indigo-400 uppercase tracking-widest flex items-center gap-2">
                           <ListTodo className="w-4 h-4" /> Daily Task Log
                       </h3>
                   </div>
                   <div className="bg-slate-900/30 border border-slate-800/50 rounded-lg p-3 flex-1 min-h-0 overflow-y-auto">
                       <ActivityTaskLogView 
                          activity={activity}
                          tasks={tasks} 
                          officers={officers}
                          issues={issues}
                          progressReports={progressReports}
                          onAddTask={() => {
                            setEditingTask(null);
                            setShowAddTaskDialog(true);
                          }}
                          onEditTask={(task) => {
                            setEditingTask(task);
                            setShowAddTaskDialog(true);
                          }}
                          onReportIssue={handleTaskStruggling}
                          currentUserEmail={currentUserEmail}
                        />
                   </div>
               </motion.div>
            )}

            {currentView === 'reports' && (
               <motion.div key="reports" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="p-4 flex flex-col h-full">
                   <div className="flex items-center gap-2 mb-4 shrink-0">
                       <Button variant="ghost" size="icon" onClick={() => setCurrentView('home')} className="h-8 w-8 hover:bg-slate-800">
                           <ArrowLeft className="w-4 h-4" />
                       </Button>
                       <h3 className="text-sm font-bold text-amber-400 uppercase tracking-widest flex items-center gap-2">
                           <FileText className="w-4 h-4" /> Progress Reports
                       </h3>
                       {currentUserEmail === activity.responsible_email && (
                           <Button 
                               size="sm" 
                               className="ml-auto bg-indigo-600 hover:bg-indigo-500 text-white h-8 text-xs font-bold shadow-lg"
                               onClick={() => setShowProgressReportModal(true)}
                           >
                               <Plus className="w-4 h-4 mr-1.5" /> Submit New
                           </Button>
                       )}
                   </div>

                   {/* KR Progress Chart */}
                   <div className="bg-slate-900/30 border border-slate-800/50 rounded-lg p-3 mb-4 shrink-0">
                       <KRProgressChart activity={activity} progressReports={progressReports} />
                   </div>

                   {/* Scrollable Reports List */}
                   <div className="flex-1 min-h-0 bg-slate-900/30 border border-slate-800/50 rounded-lg overflow-y-auto [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-slate-700/50 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-slate-600/50">
                       <div className="p-4 space-y-3">
                           {progressReports.map(report => {
                               const statusBorderColor = report.proposed_status_flag === 'green' 
                                   ? 'border-l-emerald-500' 
                                   : report.proposed_status_flag === 'red' 
                                   ? 'border-l-rose-500' 
                                   : 'border-l-amber-500';

                               return (
                                   <div key={report.id} className={cn("bg-slate-900/40 border-l-4 pl-4 py-3 text-sm hover:bg-slate-800/50 transition-colors rounded-r-lg", statusBorderColor)}>
                                        <div className="flex justify-between items-start mb-2">
                                            <div className="flex items-center gap-2">
                                                {getOfficer(report.reported_by).avatar_url && (
                                                    <img src={getOfficer(report.reported_by).avatar_url} className="w-5 h-5 rounded-full object-cover" />
                                                )}
                                                <span className="font-bold text-indigo-300">{formatOfficerName(report.reported_by)}</span>
                                            </div>
                                            <span className="text-xs text-slate-500 font-mono">{format(new Date(report.reported_at), 'PPP HH:mm')}</span>
                                        </div>

                                        <div className="grid grid-cols-1 gap-2 mb-3 bg-slate-950/30 p-2 rounded">
                                            {report.key_results_progress?.map((p, i) => {
                                                const matchingKr = (activity.key_results || []).find(kr => kr.description === p.description);
                                                const target = matchingKr?.target || '?';
                                                const current = parseFloat(p.new_value) || 0;
                                                const targetNum = parseFloat(target) || 1;
                                                const progress = targetNum > 0 ? Math.min((current / targetNum) * 100, 100) : 0;
                                                return (
                                                    <div key={i} className="text-xs text-slate-400 space-y-1">
                                                        <div className="flex items-center gap-2">
                                                            <span className="flex-1">{p.description}:</span>
                                                            <span className="font-mono text-sm text-white font-bold">{p.new_value}</span>
                                                            <span className="text-slate-500">/</span>
                                                            <span className="font-mono text-sm text-slate-400">{target}</span>
                                                        </div>
                                                        <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                                                            <div 
                                                                className={cn("h-full transition-all", progress >= 70 ? 'bg-emerald-500' : progress >= 40 ? 'bg-amber-500' : 'bg-rose-500')}
                                                                style={{ width: `${progress}%` }}
                                                            />
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        {report.issues_description && (
                                            <div className="mb-2 text-rose-300/80 text-xs flex items-start gap-2 bg-rose-950/10 p-2 rounded">
                                                <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />
                                                {report.issues_description}
                                            </div>
                                        )}

                                        {/* Tags from progress report */}
                                        {report.tags && (
                                            <div className="flex flex-wrap gap-1 mb-2">
                                                {(report.tags.tagged_officers || []).map((email, i) => {
                                                    const officer = officers.find(o => o.user_email === email);
                                                    return (
                                                        <Badge key={`off-${i}`} variant="outline" className="text-[9px] bg-indigo-500/10 text-indigo-400 border-indigo-500/30">
                                                            {officer?.callsign || email}
                                                        </Badge>
                                                    );
                                                })}
                                                {(report.tags.tagged_artifacts || []).map((name, i) => (
                                                    <Badge key={`art-${i}`} variant="outline" className="text-[9px] bg-cyan-500/10 text-cyan-400 border-cyan-500/30">
                                                        {name}
                                                    </Badge>
                                                ))}
                                                {(report.tags.tagged_rituals || []).map((name, i) => (
                                                    <Badge key={`rit-${i}`} variant="outline" className="text-[9px] bg-purple-500/10 text-purple-400 border-purple-500/30">
                                                        {name}
                                                    </Badge>
                                                ))}
                                                {(report.tags.tagged_policies || []).map((name, i) => (
                                                    <Badge key={`pol-${i}`} variant="outline" className="text-[9px] bg-amber-500/10 text-amber-400 border-amber-500/30">
                                                        {name}
                                                    </Badge>
                                                ))}
                                                {(report.tags.tagged_capabilities || []).map((name, i) => (
                                                    <Badge key={`cap-${i}`} variant="outline" className="text-[9px] bg-emerald-500/10 text-emerald-400 border-emerald-500/30">
                                                        {name}
                                                    </Badge>
                                                ))}
                                            </div>
                                        )}

                                        {report.narrative_on_principles && (
                                            <div className="text-slate-300 italic opacity-80 border-l-2 border-slate-700 pl-2">
                                                "{report.narrative_on_principles}"
                                            </div>
                                        )}

                                        <div className="mt-3 flex gap-2 items-center justify-between">
                                            <div className="flex items-center gap-1">
                                                {report.approved ? (
                                                    <Badge className="text-[9px] h-5 bg-emerald-500/20 text-emerald-400 flex items-center gap-1">
                                                        <CheckCheck className="w-3 h-3" />
                                                        Approved
                                                    </Badge>
                                                ) : (
                                                    <Badge className="text-[9px] h-5 bg-amber-500/20 text-amber-400">
                                                        Pending Approval
                                                    </Badge>
                                                )}
                                            </div>

                                            <div className="flex items-center gap-1">
                                                {/* Edit Button - Available to responsible and accountable, but not if approved */}
                                                {!report.approved && (currentUserEmail === activity.responsible_email || currentUserEmail === activity.accountable_email) && (
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        className="h-7 w-7 p-0 text-slate-400 hover:text-indigo-400 hover:bg-indigo-950/30"
                                                        onClick={() => {
                                                            setEditingReport(report);
                                                            setShowProgressReportModal(true);
                                                        }}
                                                        title="Edit report"
                                                    >
                                                        <Pencil className="w-3.5 h-3.5" />
                                                    </Button>
                                                )}

                                                {/* Archive Button - Only for accountable user */}
                                                {currentUserEmail === activity.accountable_email && (
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        className="h-7 w-7 p-0 text-slate-400 hover:text-amber-400 hover:bg-amber-950/30"
                                                        onClick={() => archiveReportMutation.mutate(report.id)}
                                                        disabled={archiveReportMutation.isPending}
                                                        title="Archive report"
                                                    >
                                                        <Archive className="w-3.5 h-3.5" />
                                                    </Button>
                                                )}

                                                {/* Remove Button - Only for accountable user */}
                                                {currentUserEmail === activity.accountable_email && (
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        className={cn(
                                                          "h-7 p-0 text-slate-400 hover:text-rose-400 hover:bg-rose-950/30",
                                                          isReportPendingConfirm(report.id) ? "w-auto px-2 bg-rose-950/30 text-rose-400" : "w-7"
                                                        )}
                                                        onClick={() => handleReportDeleteClick(report.id)}
                                                        disabled={isReportDeleting}
                                                        title={isReportPendingConfirm(report.id) ? "Click again to confirm" : "Remove report"}
                                                    >
                                                        <Trash2 className="w-3.5 h-3.5" />
                                                        {isReportPendingConfirm(report.id) && <span className="text-[10px] ml-1">Confirm?</span>}
                                                    </Button>
                                                )}

                                                {/* Approve Button - Only for accountable user on pending reports */}
                                                {!report.approved && currentUserEmail === activity.accountable_email && (
                                                    <Button
                                                        size="sm"
                                                        className="h-7 text-xs bg-emerald-600 hover:bg-emerald-500 text-white font-bold"
                                                        onClick={() => approveReportMutation.mutate(report)}
                                                        disabled={approveReportMutation.isPending}
                                                    >
                                                        <CheckCheck className="w-3 h-3 mr-1" />
                                                        Approve
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                   </div>
                               );
                           })}
                           {progressReports.length === 0 && (
                               <div className="text-center py-12 text-slate-500 italic">No progress reports submitted yet.</div>
                           )}
                       </div>
                   </div>
               </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* 7. QUICK ACTIONS (Footer - Always Visible) */}
        <div className="p-3 border-t border-slate-800 bg-[#0F172A] flex gap-2 shrink-0 shadow-[0_-5px_20px_-5px_rgba(0,0,0,0.5)] z-20">
             <Button 
                variant="outline" 
                className="flex-1 bg-amber-900/10 border-amber-900/30 text-amber-500 hover:bg-amber-900/20 h-10 text-xs font-bold uppercase tracking-wider disabled:opacity-50"
                disabled={updateActivityMutation.isPending}
                onClick={() => {
                    const promise = updateActivityMutation.mutateAsync({ status: 'planning', status_flag: 'yellow' });
                    toast.promise(promise, {
                        loading: 'Pausing activity...',
                        success: 'Activity paused',
                        error: 'Failed to pause activity'
                    });
                }}
            >
                <PauseCircle className="w-4 h-4 mr-2" /> Pause Activity
             </Button>
             
             <Button 
                variant="outline"
                className="flex-1 bg-rose-900/10 border-rose-900/30 text-rose-500 hover:bg-rose-900/20 h-10 text-xs font-bold uppercase tracking-wider"
                onClick={() => openReportModal({ title: "Off Track Report", severity: 'critical' })}
             >
                <AlertOctagon className="w-4 h-4 mr-2" /> Report Issue
             </Button>

             <Button 
                className="flex-1 bg-emerald-700 hover:bg-emerald-600 text-white shadow-[0_0_15px_-3px_rgba(16,185,129,0.4)] border border-emerald-500/50 h-10 text-xs font-bold uppercase tracking-wider disabled:opacity-50"
                disabled={updateActivityMutation.isPending}
                onClick={() => {
                    const promise = updateActivityMutation.mutateAsync({ status: 'completed', status_flag: 'green' });
                    toast.promise(promise, {
                        loading: 'Completing activity...',
                        success: 'Activity completed',
                        error: 'Failed to complete activity'
                    });
                }}
             >
                <CheckCircle2 className="w-4 h-4 mr-2" /> Activity Complete
             </Button>
        </div>
      </div>

      {/* Modals */}
      <OfficerTaskInspector 
        officer={inspectingOfficer}
        open={!!inspectingOfficer}
        onClose={() => setInspectingOfficer(null)}
      />

      <ProgressReportModal 
        activity={activity}
        open={showProgressReportModal}
        onClose={() => {
          setShowProgressReportModal(false);
          setEditingReport(null);
        }}
        onSubmit={(data) => createProgressReportMutation.mutate(data)}
        officer={getOfficer(activity.accountable_email)}
        editingReport={editingReport}
        progressReports={progressReports}
      />

      <ReportActivityIssueModal 
        activity={activity}
        open={showReportIssueModal}
        onClose={() => {
            setShowReportIssueModal(false);
            setReportIssueInitialData(null);
        }}
        initialData={reportIssueInitialData}
        officers={officers}
        artifacts={artifacts}
        rituals={rituals}
        policies={policies}
        capabilities={capabilities}
      />

      {/* Close Confirmation Dialog */}
      <AlertDialog open={showCloseConfirm} onOpenChange={setShowCloseConfirm}>
        <AlertDialogContent className="bg-slate-900 border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-slate-100">Close Activity Details?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              Are you sure?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-800 border-slate-700 text-slate-100 hover:bg-slate-700">
              No, go back
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmClose}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Yes, close
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Key Results Dialog */}
      <ConfirmableDialog 
        open={showKRDialog} 
        onOpenChange={setShowKRDialog}
        warningTitle="Close Key Results?"
        warningDescription="You'll return to the activity details."
      >
        <DialogContent data-testid="civ-activityrtsoverlay-dialog" className="bg-[#0B1221] border-slate-700 text-slate-100 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-slate-100 flex items-center gap-2">
              <Target className="w-5 h-5 text-emerald-400" />
              Key Results
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3 mt-4">
            {(activity.key_results || []).length > 0 ? (
              activity.key_results.map((kr, idx) => {
                const current = parseFloat(kr.current_value) || 0;
                const target = parseFloat(kr.target) || 1;
                const progress = target > 0 ? (current / target) * 100 : 0;
                
                return (
                  <div key={idx} className="bg-slate-900/50 border border-slate-800 rounded-lg p-4">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <p className="text-sm text-slate-200 font-medium">{kr.description}</p>
                      <div className="flex items-center gap-1 shrink-0">
                        <span className="text-lg font-bold text-emerald-400">{kr.current_value || 0}</span>
                        <span className="text-slate-600">/</span>
                        <span className="text-sm text-slate-400">{kr.target} {kr.unit || ''}</span>
                      </div>
                    </div>
                    <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                      <motion.div 
                        className={cn("h-full", progress >= 70 ? 'bg-emerald-500' : progress >= 40 ? 'bg-amber-500' : 'bg-rose-500')}
                        initial={{ width: 0 }}
                        animate={{ width: `${Math.min(progress, 100)}%` }}
                        transition={{ duration: 0.5 }}
                      />
                    </div>
                    <div className="flex justify-end mt-1">
                      <span className="text-[10px] text-slate-500 font-mono">{Math.round(progress)}%</span>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center py-8 text-slate-500 italic">
                No key results defined for this activity.
              </div>
            )}
          </div>
        </DialogContent>
      </ConfirmableDialog>

      {/* Bot Task Generation Dialog */}
      <ConfirmableDialog 
        open={showBotTaskDialog} 
        onOpenChange={setShowBotTaskDialog}
        warningTitle="Close AI Task Generation?"
        warningDescription="You'll return to the activity details."
      >
        <DialogContent data-testid="civ-activityrtsoverlay-dialog-1" className="bg-[#0B1221] border-slate-700 text-slate-100 max-w-md p-0 overflow-hidden">
          <div className="p-6">
            <BotTaskGenerationSection activity={activity} userEmail={activity.accountable_email} />
          </div>
        </DialogContent>
      </ConfirmableDialog>

      {/* Add/Edit Task Dialog */}
      <ConfirmableDialog 
        open={showAddTaskDialog} 
        onOpenChange={(open) => {
          setShowAddTaskDialog(open);
          if (!open) setEditingTask(null);
        }}
        warningTitle={editingTask ? "Close Edit Task?" : "Close Add Task?"}
        warningDescription="Your task details will be lost."
      >
        <DialogContent data-testid="civ-activityrtsoverlay-dialog-2" className="bg-[#0B1221] border-slate-700 text-slate-100 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-slate-100 flex items-center gap-2">
              <ListTodo className="w-5 h-5 text-indigo-400" />
              {editingTask ? 'Edit Task' : 'Add Task'}
            </DialogTitle>
          </DialogHeader>
          <TaskQuickAdd 
            activity={activity}
            officers={officers}
            currentUserEmail={currentUserEmail}
            existingTaskCount={tasks.length}
            editingTask={editingTask}
            onClose={() => {
              setShowAddTaskDialog(false);
              setEditingTask(null);
            }}
          />
        </DialogContent>
      </ConfirmableDialog>

      {/* Hierarchy Dialog */}
      <ConfirmableDialog 
        open={showHierarchyDialog} 
        onOpenChange={setShowHierarchyDialog}
        warningTitle="Close Hierarchy View?"
        warningDescription="You'll return to the activity details."
      >
        <DialogContent data-testid="civ-activityrtsoverlay-dialog-3" className="bg-[#0B1221] border-slate-700 text-slate-100 max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-100 flex items-center gap-2">
              <Target className="w-5 h-5 text-indigo-400" />
              Hierarchy
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {/* Mission */}
            <div className="relative">
              <div className="absolute left-4 top-12 bottom-0 w-0.5 bg-gradient-to-b from-amber-500/50 to-indigo-500/50" />
              
              <div className="bg-gradient-to-r from-amber-950/40 to-amber-900/20 border border-amber-500/30 rounded-lg p-4 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-amber-500 to-amber-600" />
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-amber-500/20 border border-amber-500/40 flex items-center justify-center shrink-0">
                    <Rocket className="w-4 h-4 text-amber-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] font-bold text-amber-400 uppercase tracking-widest">Mission</span>
                      <Badge variant="outline" className="text-[9px] border-amber-500/30 text-amber-400 h-4">
                        {parentMission?.mission_code || '...'}
                      </Badge>
                      <Badge variant="outline" className="text-[9px] border-slate-600 text-slate-400 h-4 capitalize">
                        {parentMission?.game_mode?.replace(/_/g, ' ') || '...'}
                      </Badge>
                    </div>
                    <h3 className="text-lg font-bold text-slate-100 mb-2">{parentMission?.title || 'Loading...'}</h3>
                    {parentMission?.commanders_intent && (
                      <div className="mb-2">
                        <span className="text-[10px] text-slate-500 uppercase tracking-wider">Commander's Intent</span>
                        <p className="text-sm text-slate-300 mt-1 leading-relaxed">{parentMission.commanders_intent}</p>
                      </div>
                    )}
                    {parentMission?.objective && (
                      <div className="bg-slate-950/50 rounded p-2 mt-2">
                        <span className="text-[10px] text-slate-500 uppercase tracking-wider">Mission Objective</span>
                        <p className="text-sm text-slate-400 mt-1">{parentMission.objective}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Objective */}
            <div className="relative ml-4">
              <div className="absolute left-4 top-12 bottom-0 w-0.5 bg-gradient-to-b from-indigo-500/50 to-emerald-500/50" />
              
              <div className="bg-gradient-to-r from-indigo-950/40 to-indigo-900/20 border border-indigo-500/30 rounded-lg p-4 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-indigo-500 to-indigo-600" />
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-indigo-500/20 border border-indigo-500/40 flex items-center justify-center shrink-0">
                    <Compass className="w-4 h-4 text-indigo-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] font-bold text-indigo-400 uppercase tracking-widest">Objective</span>
                      <Badge variant="outline" className="text-[9px] border-indigo-500/30 text-indigo-400 h-4">
                        {parentObjective?.objective_code || '...'}
                      </Badge>
                      <Badge variant="outline" className={cn("text-[9px] h-4 capitalize", 
                        parentObjective?.status === 'active' ? 'border-emerald-500/30 text-emerald-400' :
                        parentObjective?.status === 'pending_activation' ? 'border-amber-500/30 text-amber-400' :
                        'border-slate-600 text-slate-400'
                      )}>
                        {parentObjective?.status?.replace(/_/g, ' ') || '...'}
                      </Badge>
                    </div>
                    <h3 className="text-base font-bold text-slate-100 mb-2">{parentObjective?.statement || 'Loading...'}</h3>
                    
                    {/* SMART Breakdown */}
                    {parentObjective && (
                      <div className="grid grid-cols-2 gap-2 mt-3">
                        {parentObjective.specific && (
                          <div className="bg-slate-950/50 rounded p-2">
                            <span className="text-[9px] text-cyan-400 uppercase tracking-wider font-bold">Specific</span>
                            <p className="text-xs text-slate-400 mt-0.5 line-clamp-2">{parentObjective.specific}</p>
                          </div>
                        )}
                        {parentObjective.measurable && (
                          <div className="bg-slate-950/50 rounded p-2">
                            <span className="text-[9px] text-blue-400 uppercase tracking-wider font-bold">Measurable</span>
                            <p className="text-xs text-slate-400 mt-0.5 line-clamp-2">{parentObjective.measurable}</p>
                          </div>
                        )}
                        {parentObjective.time_bound && (
                          <div className="bg-slate-950/50 rounded p-2 col-span-2">
                            <span className="text-[9px] text-amber-400 uppercase tracking-wider font-bold">Time-bound</span>
                            <p className="text-xs text-slate-400 mt-0.5">{parentObjective.time_bound}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Activity */}
            <div className="relative ml-8">
              <div className="bg-gradient-to-r from-emerald-950/40 to-emerald-900/20 border border-emerald-500/30 rounded-lg p-4 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-emerald-500 to-emerald-600" />
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center shrink-0">
                    <Activity className="w-4 h-4 text-emerald-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] font-bold text-emerald-400 uppercase tracking-widest">Activity</span>
                      <Badge variant="outline" className="text-[9px] border-emerald-500/30 text-emerald-400 h-4">
                        {activity.activity_code || 'ACT'}
                      </Badge>
                      <Badge variant="outline" className={cn("text-[9px] h-4", getStatusColor(activity.status_flag || 'yellow'))}>
                        {activity.status_flag === 'green' ? 'On Track' : activity.status_flag === 'red' ? 'Off Track' : 'At Risk'}
                      </Badge>
                    </div>
                    <h3 className="text-base font-bold text-slate-100 mb-2">{activity.title}</h3>
                    
                    {activity.description && (
                      <p className="text-sm text-slate-400 mb-3">{activity.description}</p>
                    )}

                    {/* Key Results */}
                    {(activity.key_results || []).length > 0 && (
                      <div className="space-y-2 mt-3">
                        <span className="text-[10px] text-slate-500 uppercase tracking-wider">Key Results</span>
                        {activity.key_results.map((kr, idx) => (
                          <div key={idx} className="flex items-center justify-between bg-slate-950/50 rounded p-2">
                            <span className="text-xs text-slate-300">{kr.description}</span>
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-mono text-emerald-400">{kr.current_value || 0}</span>
                              <span className="text-xs text-slate-600">/</span>
                              <span className="text-xs font-mono text-slate-400">{kr.target}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Due Date */}
                    {activity.due_date && (
                      <div className="mt-3 flex items-center gap-2 text-sm">
                        <CalendarIcon className="w-4 h-4 text-slate-500" />
                        <span className="text-slate-400">Due {format(new Date(activity.due_date), 'MMMM d, yyyy')}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </ConfirmableDialog>
    </motion.div>
  );
}